import { type ReactNode } from "react";
import { Platform, View, type ViewStyle } from "react-native";

import { useScrollReveal } from "../../lib/useScrollReveal";

export type RevealProps = {
  variant?: "fade-up" | "fade-left" | "wipe" | "emerge";
  children: ReactNode;
  style?: ViewStyle;
};

function webRevealStyle(variant: NonNullable<RevealProps["variant"]>, isVisible: boolean): ViewStyle {
  if (variant === "fade-left") {
    return {
      opacity: isVisible ? 1 : 0,
      transform: [{ translateX: isVisible ? 0 : -30 }],
      transitionProperty: "opacity, transform",
      transitionDuration: "0.8s",
      transitionTimingFunction: "ease",
    } as unknown as ViewStyle;
  }

  if (variant === "wipe") {
    return {
      clipPath: isVisible ? "inset(0 0 0% 0)" : "inset(0 0 100% 0)",
      transitionProperty: "clip-path",
      transitionDuration: "1.1s",
      transitionTimingFunction: "cubic-bezier(0.76, 0, 0.24, 1)",
    } as unknown as ViewStyle;
  }

  if (variant === "emerge") {
    return {
      opacity: isVisible ? 1 : 0,
      filter: isVisible ? "blur(0)" : "blur(4px)",
      transitionProperty: "opacity, filter",
      transitionDuration: "1.3s",
      transitionTimingFunction: "ease",
    } as unknown as ViewStyle;
  }

  return {
    opacity: isVisible ? 1 : 0,
    transform: [{ translateY: isVisible ? 0 : 30 }],
    transitionProperty: "opacity, transform",
    transitionDuration: "0.8s",
    transitionTimingFunction: "ease",
  } as unknown as ViewStyle;
}

export function Reveal({ variant = "fade-up", children, style }: RevealProps) {
  const { ref, isVisible } = useScrollReveal();

  if (Platform.OS !== "web") {
    return <View style={style}>{children}</View>;
  }

  return <View ref={ref} style={[webRevealStyle(variant, isVisible), style]}>{children}</View>;
}
